// ============================================================================
// California Wage Theft Prevention Notice (WTPN)
// Required notice for all new California employees under Labor Code §2810.5
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FileText,
  AlertTriangle,
  DollarSign,
  Calendar,
  Clock,
  Building2,
  Users,
  Phone,
  MapPin,
  Shield,
  CheckCircle2,
  ChevronRight,
  Download,
  Printer,
  Info,
  FileCheck,
  Scale,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import ESignature, { SignatureDisplay } from '../compliance/ESignature';
import { CA_WTPN_FORM } from '@/lib/californiaForms';

// ============================================================================
// PAY FREQUENCY OPTIONS
// ============================================================================

const PAY_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly (Every 2 weeks)' },
  { value: 'semimonthly', label: 'Semi-Monthly (Twice a month)' },
  { value: 'monthly', label: 'Monthly' },
];

// ============================================================================
// PAY TYPE OPTIONS
// ============================================================================

const PAY_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'salary', label: 'Salary' },
  { value: 'piece_rate', label: 'Piece Rate' },
  { value: 'commission', label: 'Commission' },
  { value: 'other', label: 'Other' },
];

// ============================================================================
// ALLOWANCE TYPES
// ============================================================================

const ALLOWANCE_TYPES = [
  { id: 'meals', label: 'Meals', description: 'Meal credit towards minimum wage' },
  { id: 'lodging', label: 'Lodging', description: 'Housing credit towards minimum wage' },
  { id: 'none', label: 'None', description: 'No allowances claimed' },
];

// ============================================================================
// MAIN WTPN COMPONENT
// ============================================================================

export default function WageTheftNotice({
  employee,
  employer,
  existingSubmission,
  onSave,
  onSubmit,
  disabled = false,
  className = '',
}) {
  const employeeName = `${employee?.profile?.firstName || employee?.firstName || ''} ${employee?.profile?.lastName || employee?.lastName || ''}`.trim();
  
  // Check if this is an HR-issued WTPN (has issuedBy field) and merge data from the data field
  const hrIssuedData = existingSubmission?.issuedBy ? existingSubmission.data || {} : {};
  
  const [formData, setFormData] = useState({
    // Employee Information
    employeeName: hrIssuedData.employeeName || existingSubmission?.employeeName || employeeName,
    hireDate: existingSubmission?.hireDate || employee?.profile?.hireDate || employee?.hireDate || new Date().toISOString().split('T')[0],

    // Employer Information
    employerName: existingSubmission?.employerName || employer?.name || '',
    employerDBA: existingSubmission?.employerDBA || employer?.dba || '',
    employerAddress: existingSubmission?.employerAddress || employer?.address || '',
    employerCity: existingSubmission?.employerCity || employer?.city || '',
    employerState: existingSubmission?.employerState || employer?.state || 'CA',
    employerZip: existingSubmission?.employerZip || employer?.zip || '',
    employerPhone: existingSubmission?.employerPhone || employer?.phone || '',

    // Pay Information - prioritize HR-issued data
    rateOfPay: hrIssuedData.rateOfPay || existingSubmission?.rateOfPay || employee?.hourlyRate || employee?.salary || '',
    payBasis: hrIssuedData.payBasis || existingSubmission?.payBasis || employee?.payType || 'hourly',
    overtimeRate: existingSubmission?.overtimeRate || '',
    payPeriod: existingSubmission?.payPeriod || employee?.payFrequency || 'biweekly',
    payDay: existingSubmission?.payDay || employer?.payDay || '',

    // Allowances
    allowances: existingSubmission?.allowances || 'none',
    mealAllowance: existingSubmission?.mealAllowance || '',
    lodgingAllowance: existingSubmission?.lodgingAllowance || '',

    // Workers Comp
    workersCompCarrier: existingSubmission?.workersCompCarrier || employer?.workersCompCarrier || '',
    workersCompPhone: existingSubmission?.workersCompPhone || employer?.workersCompPhone || '',
    workersCompPolicy: existingSubmission?.workersCompPolicy || employer?.workersCompPolicy || '',

    // Additional info
    employerFEIN: existingSubmission?.employerFEIN || employer?.fein || '',
    
    // Include HR-issued metadata
    effectiveDate: hrIssuedData.effectiveDate || existingSubmission?.effectiveDate || '',
    previousRate: hrIssuedData.previousRate || existingSubmission?.previousRate || '',
  });

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(existingSubmission?.status === 'complete');
  
  // Check if this is an HR-issued WTPN that needs acknowledgment
  const isPendingAcknowledgment = existingSubmission?.status === 'pending_acknowledgment';
  const isHRIssued = !!existingSubmission?.issuedBy;

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Track scroll position in the notice
  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave?.({
        ...formData,
        status: 'draft',
        updatedAt: new Date().toISOString(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle submit
  const handleSubmit = () => {
    if (!hasScrolledToBottom) {
      return;
    }
    setShowSignature(true);
  };

  // Handle signature
  const handleSigned = async (signatureData) => {
    setIsSubmitting(true);
    try {
      await onSubmit?.({
        ...formData,
        signature: signatureData,
        status: 'complete',
        submittedAt: new Date().toISOString(),
        acknowledgedAt: isPendingAcknowledgment ? new Date().toISOString() : undefined,
      });
      setIsComplete(true);
      setShowSignature(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate overtime rate
  useEffect(() => {
    if (formData.payBasis === 'hourly' && formData.rateOfPay) {
      const overtimeRate = (parseFloat(formData.rateOfPay) * 1.5).toFixed(2);
      handleChange('overtimeRate', overtimeRate);
    }
  }, [formData.payBasis, formData.rateOfPay]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="h-6 w-6 text-red-600" />
            Wage Theft Prevention Notice
          </h2>
          <p className="text-gray-600 mt-1">
            California Labor Code Section 2810.5 • Required for All New Employees
          </p>
        </div>
        {isComplete && (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Acknowledged
          </Badge>
        )}
        {isPendingAcknowledgment && (
          <Badge className="bg-red-100 text-red-800 border-red-200 animate-pulse">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Action Required
          </Badge>
        )}
      </div>

      {/* HR-Issued Notice Alert */}
      {isPendingAcknowledgment && isHRIssued && (
        <Alert className="bg-orange-50 border-orange-200">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800">New Notice from HR</AlertTitle>
          <AlertDescription className="text-orange-700">
            HR has issued a new Wage Theft Prevention Notice for you
            {existingSubmission?.reason === 'pay_change' && ' due to a pay rate change'}.
            Please review the information below and acknowledge receipt by signing at the bottom.
            {existingSubmission?.issuedAt && (
              <span className="block mt-1 text-sm">
                Issued on: {new Date(existingSubmission.issuedAt).toLocaleDateString()}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Legal Notice */}
      <Alert className="bg-red-50 border-red-200">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertTitle className="text-red-800">Required Notice</AlertTitle>
        <AlertDescription className="text-red-700">
          Under California Labor Code §2810.5, employers are required to provide this notice to
          all employees at the time of hire. This notice informs you of your wages, pay schedule,
          employer information, and workers' compensation carrier. Please review carefully.
        </AlertDescription>
      </Alert>

      {!isComplete && (
        <>
          {/* Employer Information Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Employer Information</CardTitle>
                  <CardDescription>Your employer's legal business information</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-500">Legal Business Name</Label>
                  <p className="font-medium">{formData.employerName}</p>
                </div>
                {formData.employerDBA && (
                  <div>
                    <Label className="text-sm text-gray-500">Doing Business As (DBA)</Label>
                    <p className="font-medium">{formData.employerDBA}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm text-gray-500">Business Address</Label>
                <p className="font-medium">
                  {formData.employerAddress}<br />
                  {formData.employerCity}, {formData.employerState} {formData.employerZip}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-500">Phone Number</Label>
                  <p className="font-medium">{formData.employerPhone}</p>
                </div>
                {formData.employerFEIN && (
                  <div>
                    <Label className="text-sm text-gray-500">Federal Employer ID (FEIN)</Label>
                    <p className="font-medium">{formData.employerFEIN}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Employee Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Employee Information</CardTitle>
                  <CardDescription>Your employment details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-500">Employee Name</Label>
                  <p className="font-medium">{formData.employeeName}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Hire Date</Label>
                  <p className="font-medium">
                    {new Date(formData.hireDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Wage Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Rate of Pay</CardTitle>
                  <CardDescription>Your compensation information</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Pay Rate</TableCell>
                      <TableCell className="text-right text-lg font-bold text-amber-700">
                        ${parseFloat(formData.rateOfPay || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Pay Basis</TableCell>
                      <TableCell className="text-right">
                        {PAY_TYPES.find(t => t.value === formData.payBasis)?.label || formData.payBasis}
                      </TableCell>
                    </TableRow>
                    {formData.payBasis === 'hourly' && (
                      <TableRow>
                        <TableCell className="font-medium">Overtime Rate (1.5x)</TableCell>
                        <TableCell className="text-right">
                          ${parseFloat(formData.overtimeRate || 0).toFixed(2)} / hour
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell className="font-medium">Pay Frequency</TableCell>
                      <TableCell className="text-right">
                        {PAY_FREQUENCIES.find(f => f.value === formData.payPeriod)?.label || formData.payPeriod}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Pay Day</TableCell>
                      <TableCell className="text-right">{formData.payDay || 'See payroll schedule'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <Alert className="mt-4 bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  <strong>California Minimum Wage:</strong> As of January 1, {new Date().getFullYear()},
                  the California minimum wage is $16.00 per hour for all employers. Some cities and
                  counties have higher minimum wage requirements.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Allowances */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Allowances as Part of Minimum Wage</CardTitle>
                  <CardDescription>Credits claimed towards minimum wage requirements</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-gray-50 rounded-lg border">
                {formData.allowances === 'none' ? (
                  <p className="text-gray-600">
                    <CheckCircle2 className="h-4 w-4 inline mr-2 text-green-600" />
                    No allowances are being claimed as credit towards minimum wage.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formData.allowances === 'meals' && (
                      <p>Meal allowance: ${formData.mealAllowance || '0.00'} per meal</p>
                    )}
                    {formData.allowances === 'lodging' && (
                      <p>Lodging allowance: ${formData.lodgingAllowance || '0.00'} per week</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Workers' Compensation */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Workers' Compensation Insurance</CardTitle>
                  <CardDescription>Insurance carrier information for workplace injuries</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
                <div>
                  <Label className="text-sm text-gray-500">Insurance Carrier</Label>
                  <p className="font-medium">{formData.workersCompCarrier || 'Contact HR for information'}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-500">Carrier Phone Number</Label>
                    <p className="font-medium">{formData.workersCompPhone || 'Contact HR for information'}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500">Policy Number</Label>
                    <p className="font-medium">{formData.workersCompPolicy || 'Contact HR for information'}</p>
                  </div>
                </div>
              </div>

              <Alert className="mt-4 bg-red-50 border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800 text-sm">Important - Workplace Injuries</AlertTitle>
                <AlertDescription className="text-red-700 text-sm">
                  If you are injured on the job, you have the right to file a workers' compensation claim.
                  Report any workplace injuries to your supervisor immediately.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Legal Notice Content */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Scale className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Your Rights Under California Law</CardTitle>
                  <CardDescription>Please read this entire section carefully</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea
                className="h-64 border rounded-lg p-4"
                onScrollCapture={handleScroll}
              >
                <div className="prose prose-sm max-w-none space-y-4">
                  <h4 className="font-bold text-gray-900">CALIFORNIA LABOR LAW NOTICE</h4>

                  <p className="text-gray-700">
                    The California Wage Theft Prevention Act (Labor Code §2810.5) requires employers
                    to provide written notice to employees at the time of hire containing specified
                    information about employment terms.
                  </p>

                  <h5 className="font-semibold text-gray-800">Overtime Provisions:</h5>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Overtime is paid at 1.5x regular rate for hours worked over 8 in a day or 40 in a week</li>
                    <li>Double time is paid for hours worked over 12 in a day</li>
                    <li>The seventh consecutive day of work in a workweek is paid at 1.5x for the first 8 hours and double time thereafter</li>
                  </ul>

                  <h5 className="font-semibold text-gray-800">Meal and Rest Periods:</h5>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Employees working more than 5 hours are entitled to a 30-minute unpaid meal period</li>
                    <li>A second meal period is required if working more than 10 hours</li>
                    <li>Employees are entitled to a 10-minute paid rest break for every 4 hours worked</li>
                  </ul>

                  <h5 className="font-semibold text-gray-800">Sick Leave:</h5>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>You are entitled to paid sick leave under California law</li>
                    <li>Sick leave accrues at a rate of at least 1 hour for every 30 hours worked</li>
                    <li>You may use accrued sick leave beginning on your 90th day of employment</li>
                  </ul>

                  <h5 className="font-semibold text-gray-800">Wage Payment:</h5>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Wages must be paid at least twice monthly on designated paydays</li>
                    <li>Final wages for employees who quit must be paid within 72 hours</li>
                    <li>Final wages for terminated employees must be paid immediately</li>
                  </ul>

                  <h5 className="font-semibold text-gray-800">Your Right to File a Claim:</h5>
                  <p className="text-gray-700">
                    If you believe your employer has violated wage and hour laws, you may file
                    a wage claim with the Labor Commissioner's Office (DLSE) or file a civil lawsuit.
                  </p>

                  <h5 className="font-semibold text-gray-800">Retaliation Prohibited:</h5>
                  <p className="text-gray-700">
                    It is illegal for your employer to retaliate against you for filing a wage claim,
                    participating in an investigation, or exercising any right protected under
                    California labor law.
                  </p>

                  <h5 className="font-semibold text-gray-800">Contact Information:</h5>
                  <p className="text-gray-700">
                    <strong>California Division of Labor Standards Enforcement (DLSE)</strong><br />
                    Website: www.dir.ca.gov/dlse<br />
                    Phone: 1-866-924-9757
                  </p>

                  <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <strong>Notice:</strong> This notice does not create a contract of employment.
                      Employment is at-will unless otherwise specified in a written agreement.
                    </p>
                  </div>
                </div>
              </ScrollArea>

              {!hasScrolledToBottom && (
                <Alert className="mt-4 bg-yellow-50 border-yellow-200">
                  <Info className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800 text-sm">
                    Please scroll down to read the entire notice before acknowledging.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button
              onClick={handleSubmit}
              disabled={!hasScrolledToBottom}
              className={`${hasScrolledToBottom ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
            >
              <FileCheck className="h-4 w-4 mr-2" />
              I Have Read and Acknowledge This Notice
            </Button>
          </div>
        </>
      )}

      {/* Completed State */}
      {isComplete && existingSubmission?.signature && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Wage Theft Prevention Notice Acknowledged
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Employee:</span>
                <p className="font-medium">{formData.employeeName}</p>
              </div>
              <div>
                <span className="text-gray-500">Hire Date:</span>
                <p className="font-medium">{new Date(formData.hireDate).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-gray-500">Pay Rate:</span>
                <p className="font-medium">${parseFloat(formData.rateOfPay || 0).toFixed(2)}</p>
              </div>
              <div>
                <span className="text-gray-500">Pay Frequency:</span>
                <p className="font-medium">
                  {PAY_FREQUENCIES.find(f => f.value === formData.payPeriod)?.label}
                </p>
              </div>
            </div>
            <Separator />
            <SignatureDisplay signature={existingSubmission.signature} />
            <p className="text-sm text-green-700">
              Acknowledged on {new Date(existingSubmission.submittedAt).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Signature Dialog */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ESignature
            documentTitle="Wage Theft Prevention Notice"
            documentType="wage_theft_notice"
            documentId={existingSubmission?.id || 'new'}
            signerId={employee?.id}
            signerName={formData.employeeName}
            signerEmail={employee?.email || ''}
            onSign={handleSigned}
            onCancel={() => setShowSignature(false)}
            disabled={isSubmitting}
            consentText={`By signing below, I acknowledge that I have received a copy of the Wage Theft Prevention Notice as required by California Labor Code Section 2810.5.

I confirm that I have read and understand the information provided regarding my rate of pay, pay period, employer information, and workers' compensation insurance carrier.

I understand this acknowledgment does not constitute an employment contract and does not guarantee employment for any specific duration.`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// WTPN STATUS CARD (For Dashboard)
// ============================================================================

export function WTPNStatusCard({ submission, employee, onView, onEdit }) {
  const isComplete = submission?.status === 'complete';

  return (
    <Card className={`transition-all hover:shadow-md ${isComplete ? 'border-green-200' : ''}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100' : 'bg-red-100'
              }`}>
              <Scale className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Wage Theft Prevention Notice</h4>
              <p className="text-sm text-gray-500">California Labor Code §2810.5</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isComplete ? (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Acknowledged
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Required
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={() => isComplete ? onView?.(submission) : onEdit?.(submission)}>
              {isComplete ? 'View' : 'Review & Sign'}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {isComplete && submission?.submittedAt && (
          <p className="text-xs text-gray-500 mt-3">
            Acknowledged: {new Date(submission.submittedAt).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
